<template>
	<article v-if="article.content" class="isolate relative group">
		<div
			class="
				relative
				rounded-2.5xl
				overflow-hidden
				aspect-square
				sm:aspect-[3/2]
				md:aspect-square
				"
		>
			<NuxtPicture
				v-if="article.content.image?.filename"
				provider="storyblok"
				width="600"
				height="600"
				:modifiers="storyblokFocusModifiers(article.content.image.focus)"
				:src="article.content.image.filename"
				:alt="article.content.image.alt || ''"
				sizes="sm:100vw md:560px lg:410px xl:25vw xxl:25vw inf:420px"
				class="absolute inset-0"
				:img-attrs="{class:'cover-parent rounded-2.5xl object-cover w-full h-full'}"
				loading="lazy"
			/>
			<div class="absolute inset-0 pointer-events-none">
				<ClientOnly>
					<ArticlePreviewTilePills
						:tags="sortedPrimaryTagsByQuery"
						:tag-route-generator="tagRouteGenerator"
						:author="article.content.author"
						class="absolute bottom-3 inset-x-3 z-10 pointer-events-auto"
					/>
				</ClientOnly>
			</div>
		</div>
		<div class="mt-3 mx-3 md:mt-4 md:mx-4">
			<ClientOnly>
				<div class="flex gap-4 flex-wrap items-center mb-2 md:mb-3">
					<div v-if="article.content.dateOfPublication" class="flex items-center gap-2 text-body-tertiary">
						<NuxtIcon name="calendar" class="-mt-0.5" filled />
						<span class="typo-body-s">{{ formatDate(article.content.dateOfPublication) }}</span>
					</div>
					<div v-if="preparationTime" class="flex items-center gap-1.5 text-body-tertiary">
						<NuxtIcon name="clock" filled />
						<span class="typo-body-s">{{ preparationTime.name }}</span>
					</div>
					<div v-else-if="readingTime" class="flex items-center gap-1.5 text-body-tertiary">
						<NuxtIcon name="clock" filled />
						<span class="typo-body-s">{{ t('plural.reading', readingTime ?? 0) }}</span>
					</div>
				</div>
			</ClientOnly>
			<h3 class="typo-h2 text-heading-primary max-sm:line-clamp-3">
				{{ article.content.title }}
			</h3>
			<div v-if="!hidePerex && article.content.perex || $slots.description" class="max-sm:hidden mt-6 relative z-10">
				<slot name="description">
					{{ article.content.perex }}
				</slot>
			</div>
			<AbsoluteInternalLink
				v-if="linkLabel"
				:to="article.full_slug || '#'"
				class="before:cover-parent !font-bold group"
				:class="{'btn btn-tertiary btn-lg md:btn-lg mt-4 z-10': hasExplicitLinkLabel}"
				:aria-label="linkAriaLabel || `${linkLabel || 'Zobrazit článek'} ${article.content.title}`"
			>
				<slot name="link">
					<NuxtIcon name="custom/arrow-right" class="text-sm" filled />
					{{ linkLabel }}
				</slot>
			</AbsoluteInternalLink>
		</div>
	</article>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {find} from 'lodash-es';
	import type {ISbStoryData} from 'storyblok-js-client';
	import type {StoryblokStory} from 'storyblok-generate-ts';
	import {format} from 'date-fns';
	import {useStoryblokDatasourcesStore} from '~/stores/storyblokDatasources';
	import {nonNullable} from '~/utils';
	import type {ISbDatasourceEntry} from '~/types/storyblok';
	import type {
		PageTypeBlogArticleDetailStoryblok,
		PageTypeBusinessUnitRentalDetailStoryblok,
		PageTypePressReleaseDetailStoryblok,
		PageTypeRecipesAuthorDetailStoryblok,
		PageTypeRecipesRecipeDetailStoryblok,
	} from '~/storyblok/types';

	type ArticleContent
		= | PageTypeBlogArticleDetailStoryblok
			| PageTypeBusinessUnitRentalDetailStoryblok
			| PageTypePressReleaseDetailStoryblok
			| PageTypeRecipesRecipeDetailStoryblok
			| PageTypeRecipesAuthorDetailStoryblok;

	interface Props {
		article: ISbStoryData<ArticleContent> | StoryblokStory<ArticleContent>
		linkLabel?: string
		linkAriaLabel?: string
		hidePerex?: boolean
	}

	type Slots = {
		description(): any
		link(): any
	};

	const props = defineProps<Props>();
	const slots = defineSlots<Slots>();

	const route = useRoute();
	const {t} = useI18n();

	const datasourcesStore = useStoryblokDatasourcesStore();
	const {tags: tagEntries} = storeToRefs(datasourcesStore);

	const preparationTime = computed(() => {
		const filteredEntries = datasourcesStore.preparationTime?.filter((entry) => entry.value === props.article.content.preparationTime);
		return filteredEntries ? filteredEntries.shift() : null;
	});
	const readingTime = computed(() => useReadingTime(props.article.content.body?.[0].content, props.article.content.perex));

	const routeTags = computed(() => route.query?.tags);
	const routerPrimaryTag = computed(() => typeof routeTags.value === 'string' ? routeTags.value.split(',')[0] : undefined);
	const sortedPrimaryTagsByQuery = computed<ISbDatasourceEntry[]>(() => {
		const listOfTags = props.article.content?.listOfTags?.slice(0) ?? [];

		const index = listOfTags.indexOf(routerPrimaryTag.value);

		if (index !== -1) {
			listOfTags.splice(index, 1);
			listOfTags.unshift(routerPrimaryTag.value);
		}

		return listOfTags
			.map((tag: any | string) => find(tagEntries.value ?? [], ['value', tag]))
			.filter(nonNullable) ?? [];
	});

	const hasExplicitLinkLabel = computed(() => !!props.linkLabel || !!slots.link);

	function tagRouteGenerator(tag: ISbDatasourceEntry) {
		return {name: 'recepty-vsechny-recepty', query: {tags: tag.value}};
	}

	function formatDate(publishingDate: string) {
		return format(new Date(publishingDate), 'dd. MM. yyyy');
	}
</script>
